import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Box,
  Typography,
  Container,
  Button,
  TextField,
  Paper,
  InputAdornment,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Collapse,
  LinearProgress,
  Switch,
  FormControlLabel,
  Snackbar,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import DeleteIcon from '@mui/icons-material/Delete';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import SearchIcon from '@mui/icons-material/Search';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import Layout from '../components/Layout';
import useStore from '../store/useStore';

// Must match routes/wordImages.js (multer limits + fileFilter)
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
const MAX_BYTES = 3 * 1024 * 1024;
// Speed mode (FlashcardAssessment) caches the dictionary here
const CACHE_KEY = 'wordImageCache';
const TEMPLATE_PREF_KEY = 'dictionaryTemplateId';

const norm = (s) =>
  String(s || '')
    .toLowerCase()
    .trim();

// Same key the server and speed mode use: "word|meaning", or the plain word.
const imageKey = (img) => {
  const w = norm(img.word);
  const m = norm(img.meaning);
  return m ? `${w}|${m}` : w;
};

// Mirrors FlashcardAssessment's lookup: word|meaning first, then the plain word.
const lookupImage = (map, word, meaning) =>
  (meaning && map.get(`${word}|${meaning}`)) || map.get(word) || null;

const invalidateCache = () => {
  try {
    sessionStorage.removeItem(CACHE_KEY);
  } catch (_) {}
};

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

const retryDelay = (res, attempt) => {
  const retryAfter = parseInt(res.headers.get('Retry-After') || '', 10);
  return Number.isFinite(retryAfter) ? retryAfter * 1000 : 2000 * attempt;
};

const deriveWord = (filename) => {
  const dot = filename.lastIndexOf('.');
  const base = dot > 0 ? filename.slice(0, dot) : filename;
  return base.replace(/[_-]+/g, ' ').trim().toLowerCase();
};

const plural = (n, one, few, many) => {
  const m10 = n % 10;
  const m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return one;
  if (m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14)) return few;
  return many;
};

// Course books first ("Cherry Pick …", "Level …" in natural order),
// then everything else newest first.
const PRIORITY_RE = /^(cherry[\s-]*pick|level)/i;
const sortTemplates = (list) =>
  [...list].sort((a, b) => {
    const pa = PRIORITY_RE.test((a.name || '').trim()) ? 0 : 1;
    const pb = PRIORITY_RE.test((b.name || '').trim()) ? 0 : 1;
    if (pa !== pb) return pa - pb;
    if (pa === 1) {
      const ta = Date.parse(a.createdAt || a.updatedAt || '') || 0;
      const tb = Date.parse(b.createdAt || b.updatedAt || '') || 0;
      if (ta !== tb) return tb - ta;
    }
    return (a.name || '').localeCompare(b.name || '', undefined, {
      numeric: true,
      sensitivity: 'base',
    });
  });

const UNIT_PREFIX_RE =
  /^(unit|lesson|module|theme|юнит|урок|модуль|тема)(\s|\d|$)/i;
const unitLabel = (theme, idx) => {
  const name = (theme.name || '').trim();
  if (!name) return `Unit ${idx + 1}`;
  return UNIT_PREFIX_RE.test(name) ? name : `Unit ${idx + 1} - ${name}`;
};

const resolveItem = (item, imageMap, meaningsByWord) => {
  const w = norm(item.text);
  const m = norm(item.meaning);
  const entry = lookupImage(imageMap, w, m);
  const entryMeaning = entry ? norm(entry.meaning) : '';
  // A new image is saved with the item's meaning only when the same word means
  // different things across units; otherwise the plain word is enough and
  // speed mode finds it everywhere.
  const ambiguous = (meaningsByWord.get(w)?.size || 0) > 1;
  const meaning = entryMeaning || (ambiguous ? m : '');
  return {
    w,
    m,
    entry,
    target: { key: meaning ? `${w}|${meaning}` : w, word: w, meaning },
    // Plain-word image shown for an item that should get its own one
    fallback: !!entry && !entryMeaning && !!meaning,
  };
};

const templateCoverage = (tpl, imageMap) => {
  const seen = new Map();
  (tpl.themes || []).forEach((th) =>
    (th.items || []).forEach((it) => {
      if (it.type === 'phrase') return;
      const w = norm(it.text);
      if (!w) return;
      const m = norm(it.meaning);
      seen.set(`${w}|${m}`, !!lookupImage(imageMap, w, m));
    })
  );
  let done = 0;
  seen.forEach((has) => {
    if (has) done += 1;
  });
  const total = seen.size;
  return { total, done, pct: total ? Math.round((done / total) * 100) : 0 };
};

const SERVER_MESSAGES_RU = {
  'No image uploaded': 'Изображение не выбрано',
  'word is required': 'Укажите слово',
  'This word already has a global image set by admin. You cannot override it.':
    'У этого слова уже есть общая картинка от администратора — заменить её нельзя.',
  'Only image files are allowed (jpeg, png, gif, webp)':
    'Можно загружать только изображения (jpeg, png, gif, webp)',
  'File too large': 'Файл больше 3 МБ',
  'Global image set by admin — cannot delete.':
    'Общая картинка от администратора — удалить нельзя.',
  'Not found': 'Не найдено',
  'Too many upload requests.': 'Слишком много запросов — подождите минуту.',
  Unauthorized: 'Сессия истекла — войдите заново',
};

const localizeError = (message, language) =>
  language === 'en' ? message : SERVER_MESSAGES_RU[message] || message;

const validateFile = (file, language) => {
  if (!file || !ALLOWED_TYPES.includes(file.type))
    return language === 'en'
      ? 'Only image files are allowed (jpeg, png, gif, webp)'
      : 'Можно загружать только изображения (jpeg, png, gif, webp)';
  if (file.size > MAX_BYTES)
    return language === 'en'
      ? 'File is larger than 3 MB'
      : 'Файл больше 3 МБ — уменьшите изображение';
  return '';
};

const TILE_GRID_SX = {
  display: 'grid',
  gridTemplateColumns: {
    xs: 'repeat(3, minmax(0, 1fr))',
    sm: 'repeat(5, minmax(0, 1fr))',
    md: 'repeat(7, minmax(0, 1fr))',
    lg: 'repeat(8, minmax(0, 1fr))',
  },
  columnGap: { xs: 1, sm: 1.25 },
  rowGap: { xs: 1.25, sm: 1.5 },
};

const ACTION_BTN_SX = {
  width: 26,
  height: 26,
  bgcolor: 'background.paper',
  color: 'text.primary',
  boxShadow: '0 1px 4px rgba(0,0,0,0.2)',
  '&:hover': { bgcolor: 'background.paper', color: 'primary.main' },
};

function WordTile({
  word,
  meaning,
  imageUrl,
  isGlobal,
  isPhrase,
  canUpload,
  canDelete,
  busy,
  hint,
  note,
  replaceTitle,
  language,
  onPick,
  onFile,
  onDelete,
}) {
  const [over, setOver] = useState(false);
  const droppable = canUpload && !busy;
  const clickable = droppable && !imageUrl;

  const dropProps = droppable
    ? {
        onDragOver: (e) => {
          e.preventDefault();
          e.dataTransfer.dropEffect = 'copy';
          if (!over) setOver(true);
        },
        onDragLeave: (e) => {
          if (!e.currentTarget.contains(e.relatedTarget)) setOver(false);
        },
        onDrop: (e) => {
          e.preventDefault();
          setOver(false);
          const files = Array.from(e.dataTransfer.files || []);
          const file =
            files.find((f) => f.type.startsWith('image/')) || files[0];
          if (file) onFile(file);
        },
      }
    : {};

  return (
    <Box
      {...dropProps}
      sx={{
        minWidth: 0,
        opacity: isPhrase ? 0.8 : 1,
        '@media (hover: hover)': {
          '& .tile-actions': { opacity: 0 },
          '&:hover .tile-actions, &:focus-within .tile-actions': { opacity: 1 },
        },
      }}
    >
      <Box
        role={clickable ? 'button' : undefined}
        tabIndex={clickable ? 0 : undefined}
        aria-label={
          clickable
            ? `${language === 'en' ? 'Add image' : 'Добавить картинку'}: ${word}`
            : undefined
        }
        onClick={clickable ? onPick : undefined}
        onKeyDown={
          clickable
            ? (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onPick();
                }
              }
            : undefined
        }
        sx={(theme) => ({
          position: 'relative',
          aspectRatio: '1 / 1',
          borderRadius: '12px',
          overflow: 'hidden',
          border: imageUrl ? '1px solid' : '1.5px dashed',
          borderColor: over
            ? theme.palette.primary.main
            : imageUrl
              ? theme.palette.divider
              : alpha(theme.palette.text.primary, isPhrase ? 0.14 : 0.24),
          bgcolor: over
            ? alpha(theme.palette.primary.main, 0.08)
            : imageUrl
              ? '#fff'
              : 'transparent',
          cursor: clickable ? 'pointer' : 'default',
          outline: 'none',
          transition: 'border-color 0.15s ease, background-color 0.15s ease',
          ...(clickable && {
            '&:hover, &:focus-visible': {
              borderColor: theme.palette.primary.main,
              bgcolor: alpha(theme.palette.primary.main, 0.05),
            },
            '&:hover .tile-ph, &:focus-visible .tile-ph': {
              color: theme.palette.primary.main,
            },
          }),
        })}
      >
        {imageUrl ? (
          <Box
            component="img"
            src={imageUrl}
            alt={word}
            loading="lazy"
            draggable={false}
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'contain',
              display: 'block',
            }}
          />
        ) : (
          <Box
            className="tile-ph"
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 0.25,
              px: 0.5,
              textAlign: 'center',
              color: over ? 'primary.main' : 'text.disabled',
              transition: 'color 0.15s ease',
            }}
          >
            <AddPhotoAlternateIcon sx={{ fontSize: 22 }} />
            {droppable && (
              <Typography
                component="span"
                sx={{ fontSize: '0.68rem', fontWeight: 600, lineHeight: 1.2 }}
              >
                {isPhrase
                  ? language === 'en'
                    ? 'optional'
                    : 'необязательно'
                  : language === 'en'
                    ? '+ Add'
                    : '+ Добавить'}
              </Typography>
            )}
          </Box>
        )}

        {isGlobal && (
          <Box
            component="span"
            title={
              language === 'en'
                ? 'Global image (uploaded by admin)'
                : 'Общая картинка (загружена администратором)'
            }
            sx={(theme) => ({
              position: 'absolute',
              top: 4,
              left: 4,
              px: 0.6,
              borderRadius: '6px',
              fontSize: '0.6rem',
              fontWeight: 700,
              lineHeight: '16px',
              color: '#fff',
              bgcolor: alpha(theme.palette.secondary.main, 0.92),
            })}
          >
            {language === 'en' ? 'global' : 'общая'}
          </Box>
        )}

        {busy && (
          <Box
            sx={(theme) => ({
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: alpha(theme.palette.background.paper, 0.7),
            })}
          >
            <CircularProgress size={22} />
          </Box>
        )}

        {imageUrl && !busy && (canUpload || canDelete) && (
          <Box
            className="tile-actions"
            sx={{
              position: 'absolute',
              top: 4,
              right: 4,
              display: 'flex',
              gap: 0.5,
              transition: 'opacity 0.15s ease',
            }}
          >
            {canUpload && (
              <Tooltip
                title={
                  replaceTitle ||
                  (language === 'en' ? 'Replace image' : 'Заменить картинку')
                }
              >
                <IconButton size="small" onClick={onPick} sx={ACTION_BTN_SX}>
                  <AutorenewIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            )}
            {canDelete && (
              <Tooltip title={language === 'en' ? 'Delete' : 'Удалить'}>
                <IconButton
                  size="small"
                  onClick={onDelete}
                  sx={{
                    ...ACTION_BTN_SX,
                    '&:hover': {
                      bgcolor: 'background.paper',
                      color: 'error.main',
                    },
                  }}
                >
                  <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        )}
      </Box>

      <Box title={hint || undefined} sx={{ pt: 0.5, px: 0.25 }}>
        <Typography
          sx={{
            fontSize: '0.8rem',
            fontWeight: isPhrase ? 500 : 700,
            fontStyle: isPhrase ? 'italic' : 'normal',
            lineHeight: 1.25,
            wordBreak: 'break-word',
            overflow: 'hidden',
            display: '-webkit-box',
            WebkitBoxOrient: 'vertical',
            WebkitLineClamp: isPhrase ? 2 : 1,
          }}
        >
          {word}
        </Typography>
        {meaning && (
          <Typography
            noWrap
            sx={{
              fontSize: '0.7rem',
              color: 'text.secondary',
              lineHeight: 1.3,
            }}
          >
            {meaning}
          </Typography>
        )}
        {note && (
          <Typography
            noWrap
            sx={{
              fontSize: '0.65rem',
              fontWeight: 600,
              lineHeight: 1.3,
              color: (theme) =>
                theme.palette.mode === 'dark'
                  ? theme.palette.warning.light
                  : theme.palette.warning.dark,
            }}
          >
            {note}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

export default function DictionaryPage() {
  const { language, userRole } = useStore();
  const isAdmin = userRole === 'admin';

  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(true);
  const [templatesError, setTemplatesError] = useState(false);
  const [view, setView] = useState('programs'); // 'programs' | 'all'
  const [templateId, setTemplateId] = useState(() => {
    try {
      return localStorage.getItem(TEMPLATE_PREF_KEY) || '';
    } catch (_) {
      return '';
    }
  });
  const [filter, setFilter] = useState('all'); // 'all' | 'missing' | 'done'
  const [showPhrases, setShowPhrases] = useState(false);
  const [collapsed, setCollapsed] = useState({}); // unit key → bool (unset = auto)
  const [strayOpen, setStrayOpen] = useState(true);
  const [search, setSearch] = useState('');
  const [queue, setQueue] = useState([]); // [{id, file, word, meaning, preview, status, error}]
  const [uploading, setUploading] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [busy, setBusy] = useState({}); // image key → true while saving/deleting
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [toast, setToast] = useState({
    open: false,
    severity: 'success',
    text: '',
  });
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);
  const tileInputRef = useRef(null);
  const tileTargetRef = useRef(null);

  const showToast = (severity, text) =>
    setToast({ open: true, severity, text });

  const setBusyKey = (key, on) =>
    setBusy((prev) => {
      const next = { ...prev };
      if (on) next[key] = true;
      else delete next[key];
      return next;
    });

  const addFiles = (files) => {
    const list = Array.from(files).filter((f) => f.type.startsWith('image/'));
    if (!list.length) return;
    list.forEach((f) => {
      const problem = validateFile(f, language);
      const reader = new FileReader();
      reader.onload = (e) => {
        setQueue((prev) => [
          ...prev,
          {
            id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
            file: f,
            word: deriveWord(f.name),
            meaning: '',
            preview: e.target.result,
            status: problem ? 'error' : 'pending',
            error: problem,
          },
        ]);
      };
      reader.readAsDataURL(f);
    });
  };

  const load = (silent = false) => {
    invalidateCache();
    if (!silent) setLoading(true);
    fetch('/api/word-images', { credentials: 'include' })
      .then((r) => r.json())
      .then((data) => setImages(Array.isArray(data) ? data : []))
      .catch(() =>
        setError(
          language === 'en'
            ? 'Failed to load dictionary'
            : 'Не удалось загрузить словарь'
        )
      )
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    fetch('/api/templates', { credentials: 'include' })
      .then((r) => r.json())
      .then((data) => {
        if (Array.isArray(data)) setTemplates(data);
        else setTemplatesError(true);
      })
      .catch(() => setTemplatesError(true))
      .finally(() => setTemplatesLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // A file dropped next to a tile must not make the browser open it.
  useEffect(() => {
    const blockStrayDrop = (e) => {
      if (e.defaultPrevented) return;
      const types = Array.from((e.dataTransfer && e.dataTransfer.types) || []);
      if (!types.includes('Files')) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = 'none';
    };
    window.addEventListener('dragover', blockStrayDrop);
    window.addEventListener('drop', blockStrayDrop);
    return () => {
      window.removeEventListener('dragover', blockStrayDrop);
      window.removeEventListener('drop', blockStrayDrop);
    };
  }, []);

  // ── Derived data ───────────────────────────────────────────────────────────
  const imageMap = useMemo(() => {
    const map = new Map();
    images.forEach((img) => map.set(imageKey(img), img));
    return map;
  }, [images]);

  const sortedImages = useMemo(
    () =>
      [...images].sort(
        (a, b) =>
          norm(a.word).localeCompare(norm(b.word)) ||
          norm(a.meaning).localeCompare(norm(b.meaning))
      ),
    [images]
  );

  const sortedTemplates = useMemo(
    () =>
      sortTemplates(
        templates.filter((tpl) =>
          (tpl.themes || []).some((th) =>
            (th.items || []).some((it) => norm(it.text))
          )
        )
      ),
    [templates]
  );

  // word → every meaning it has across all programs
  const meaningsByWord = useMemo(() => {
    const map = new Map();
    templates.forEach((tpl) =>
      (tpl.themes || []).forEach((th) =>
        (th.items || []).forEach((it) => {
          const w = norm(it.text);
          if (!w) return;
          if (!map.has(w)) map.set(w, new Set());
          map.get(w).add(norm(it.meaning));
        })
      )
    );
    return map;
  }, [templates]);

  // Which image entries speed mode actually shows for some program item
  const usage = useMemo(() => {
    const used = new Map(); // image key → ["Level 1 · Unit 3 - Food", …]
    const words = new Set();
    templates.forEach((tpl) =>
      (tpl.themes || []).forEach((th, ti) =>
        (th.items || []).forEach((it) => {
          const w = norm(it.text);
          if (!w) return;
          words.add(w);
          const entry = lookupImage(imageMap, w, norm(it.meaning));
          if (!entry) return;
          const k = imageKey(entry);
          const where = `${tpl.name} · ${unitLabel(th, ti)}`;
          if (!used.has(k)) used.set(k, []);
          if (!used.get(k).includes(where)) used.get(k).push(where);
        })
      )
    );
    return { used, words };
  }, [templates, imageMap]);

  const templatesReady = !templatesLoading && !templatesError;

  const strays = useMemo(() => {
    if (templatesLoading || templatesError) return [];
    return sortedImages
      .filter((img) => !usage.used.has(imageKey(img)))
      .map((img) => {
        let reason = 'shadowed';
        if (!usage.words.has(norm(img.word))) reason = 'noword';
        else if (norm(img.meaning)) reason = 'meaning';
        return { img, reason };
      });
  }, [sortedImages, usage, templatesLoading, templatesError]);

  const coverageById = useMemo(() => {
    const out = {};
    sortedTemplates.forEach((tpl) => {
      out[tpl._id] = templateCoverage(tpl, imageMap);
    });
    return out;
  }, [sortedTemplates, imageMap]);

  const selectedTemplate =
    sortedTemplates.find((tpl) => tpl._id === templateId) ||
    sortedTemplates[0] ||
    null;

  const units = useMemo(() => {
    if (!selectedTemplate) return [];
    return (selectedTemplate.themes || []).map((th, ti) => {
      const label = unitLabel(th, ti);
      const tiles = (th.items || [])
        .filter((it) => norm(it.text))
        .map((it, ii) => ({
          id: it._id || `${ti}-${ii}`,
          text: String(it.text).trim(),
          meaning: String(it.meaning || '').trim(),
          isPhrase: it.type === 'phrase',
          ...resolveItem(it, imageMap, meaningsByWord),
        }));
      const words = tiles.filter((t) => !t.isPhrase);
      return {
        key: `${selectedTemplate._id}:${th._id || ti}`,
        label,
        labelLower: label.toLowerCase(),
        tiles,
        total: words.length,
        withImage: words.filter((t) => t.entry).length,
      };
    });
  }, [selectedTemplate, imageMap, meaningsByWord]);

  const selectTemplate = (id) => {
    setTemplateId(id);
    try {
      localStorage.setItem(TEMPLATE_PREF_KEY, id);
    } catch (_) {}
  };

  // ── Server calls ───────────────────────────────────────────────────────────
  const postImage = async (file, word, meaning, onWait, attempt = 1) => {
    const fd = new FormData();
    fd.append('word', word.trim().toLowerCase());
    fd.append('meaning', (meaning || '').trim().toLowerCase());
    fd.append('image', file);
    const res = await fetch('/api/word-images', {
      method: 'POST',
      credentials: 'include',
      body: fd,
    });
    if (res.status === 429 && attempt <= 5) {
      const waitMs = retryDelay(res, attempt);
      if (onWait) onWait(waitMs);
      await sleep(waitMs);
      return postImage(file, word, meaning, onWait, attempt + 1);
    }
    if (!res.ok) {
      const d = await res.json().catch(() => ({}));
      throw new Error(
        localizeError(d.message || `HTTP ${res.status}`, language)
      );
    }
    return res.json().catch(() => null);
  };

  // The server deletes by word; meaning is sent so a meaning-aware backend can
  // target one entry. 404 means it is already gone.
  const deleteRequest = async (img, attempt = 1) => {
    const meaning = norm(img.meaning);
    const qs = meaning ? `?meaning=${encodeURIComponent(meaning)}` : '';
    const res = await fetch(
      `/api/word-images/${encodeURIComponent(norm(img.word))}${qs}`,
      { method: 'DELETE', credentials: 'include' }
    );
    if (res.status === 429 && attempt <= 5) {
      await sleep(retryDelay(res, attempt));
      return deleteRequest(img, attempt + 1);
    }
    if (res.ok || res.status === 404) return;
    const d = await res.json().catch(() => ({}));
    throw new Error(localizeError(d.message || `HTTP ${res.status}`, language));
  };

  const waitText = (waitMs) =>
    language === 'en'
      ? `Rate limited — retry in ${Math.ceil(waitMs / 1000)}s…`
      : `Ожидание ${Math.ceil(waitMs / 1000)}с…`;

  // ── Bulk queue ─────────────────────────────────────────────────────────────
  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files?.length) addFiles(e.dataTransfer.files);
  };

  const updateQueueItem = (id, patch) =>
    setQueue((prev) => prev.map((q) => (q.id === id ? { ...q, ...patch } : q)));

  const removeQueueItem = (id) =>
    setQueue((prev) => prev.filter((q) => q.id !== id));

  const uploadOne = (item) =>
    postImage(item.file, item.word, item.meaning, (waitMs) =>
      updateQueueItem(item.id, { status: 'uploading', error: waitText(waitMs) })
    );

  const handleUpload = async () => {
    const ready = queue.filter((q) => q.status !== 'done');
    if (!ready.length) {
      setError(language === 'en' ? 'Add images first' : 'Добавьте изображения');
      return;
    }
    const missing = ready.find((q) => !q.word.trim());
    if (missing) {
      setError(
        language === 'en'
          ? 'Every item needs a word'
          : 'У каждого изображения должно быть слово'
      );
      return;
    }
    setUploading(true);
    setError('');
    let okCount = 0;
    for (const item of ready) {
      const problem = validateFile(item.file, language);
      if (problem) {
        updateQueueItem(item.id, { status: 'error', error: problem });
        continue;
      }
      updateQueueItem(item.id, { status: 'uploading', error: '' });
      try {
        await uploadOne(item);
        updateQueueItem(item.id, { status: 'done', error: '' });
        okCount += 1;
        // Mild throttle to stay under rate limit on long queues
        await sleep(250);
      } catch (err) {
        updateQueueItem(item.id, { status: 'error', error: err.message });
      }
    }
    setUploading(false);
    if (okCount > 0) {
      setSuccess(
        language === 'en'
          ? `${okCount} image(s) saved!`
          : `Сохранено: ${okCount}`
      );
      setTimeout(() => setSuccess(''), 3000);
      setQueue((prev) => prev.filter((q) => q.status !== 'done'));
      load(true);
    }
  };

  const queueHint = (q) => {
    if (!templatesReady || q.status !== 'pending') return null;
    const w = norm(q.word);
    if (!w) return null;
    if (!usage.words.has(w))
      return {
        color: 'warning.main',
        text: language === 'en' ? 'not in any program' : 'нет в программах',
      };
    const meanings = Array.from(meaningsByWord.get(w) || []).filter(Boolean);
    if (meanings.length > 1 && !norm(q.meaning))
      return {
        color: 'text.secondary',
        text: `${language === 'en' ? 'meanings' : 'значения'}: ${meanings.join(', ')}`,
      };
    return {
      color: 'success.main',
      text: language === 'en' ? 'in a program' : 'есть в программе',
    };
  };

  // ── Tile actions ───────────────────────────────────────────────────────────
  const uploadForTile = async (target, file) => {
    const problem = validateFile(file, language);
    if (problem) {
      showToast('error', problem);
      return;
    }
    // Keep the unit open even if this upload completes it
    if (target.unitKey)
      setCollapsed((prev) =>
        target.unitKey in prev ? prev : { ...prev, [target.unitKey]: false }
      );
    const label = target.meaning
      ? `${target.word} (${target.meaning})`
      : target.word;
    setBusyKey(target.key, true);
    try {
      const doc = await postImage(file, target.word, target.meaning, (waitMs) =>
        showToast('info', waitText(waitMs))
      );
      invalidateCache();
      if (doc && doc.word && doc.imageUrl) {
        const saved = { ...doc, readOnly: false };
        const key = imageKey(saved);
        setImages((prev) => [
          ...prev.filter((i) => imageKey(i) !== key),
          saved,
        ]);
      } else {
        load(true);
      }
      showToast(
        'success',
        language === 'en'
          ? `Image saved: ${label}`
          : `Картинка сохранена: ${label}`
      );
    } catch (err) {
      showToast('error', err.message);
    } finally {
      setBusyKey(target.key, false);
    }
  };

  const openPicker = (target) => {
    tileTargetRef.current = target;
    tileInputRef.current?.click();
  };

  const handleDelete = async (img) => {
    const word = norm(img.word);
    const meaning = norm(img.meaning);
    const label = meaning ? `${word} (${meaning})` : word;
    const siblings = images.filter(
      (i) => norm(i.word) === word && !i.readOnly
    ).length;
    let text =
      language === 'en'
        ? `Delete image for "${label}"?`
        : `Удалить изображение для "${label}"?`;
    if (isAdmin) {
      text +=
        language === 'en'
          ? `\n\nThe server removes every image of "${word}" (all meanings, all teachers).`
          : `\n\nСервер удалит все картинки слова "${word}" (все значения, у всех учителей).`;
    } else if (siblings > 1) {
      text +=
        language === 'en'
          ? `\n\n"${word}" has ${siblings} of your images (different meanings). The server deletes by word, so another one of them may be removed.`
          : `\n\nУ слова "${word}" несколько ваших картинок (${siblings}). Сервер удаляет по слову, поэтому может удалиться другая из них.`;
    }
    if (!window.confirm(text)) return;
    const key = imageKey(img);
    setBusyKey(key, true);
    try {
      await deleteRequest(img);
      invalidateCache();
      setImages((prev) => prev.filter((i) => imageKey(i) !== key));
      // Re-read: another entry may have been removed, or a global one uncovered
      load(true);
    } catch (err) {
      showToast('error', err.message);
    } finally {
      setBusyKey(key, false);
    }
  };

  const handleDeleteAll = async () => {
    const own = images.filter((i) => !i.readOnly).length;
    if (!own) return;
    const text = isAdmin
      ? language === 'en'
        ? `Delete ALL ${own} dictionary images (including global ones)? This cannot be undone.`
        : `Удалить ВСЕ изображения словаря (${own}), включая общие? Это необратимо.`
      : language === 'en'
        ? `Delete all ${own} of your images? Global images stay. This cannot be undone.`
        : `Удалить все ваши изображения (${own})? Общие останутся. Это необратимо.`;
    if (!window.confirm(text)) return;
    try {
      const res = await fetch('/api/word-images', {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(
          localizeError(d.message || `HTTP ${res.status}`, language)
        );
      }
      const data = await res.json();
      invalidateCache();
      showToast(
        'success',
        language === 'en'
          ? `Deleted ${data.deletedCount} images`
          : `Удалено: ${data.deletedCount}`
      );
      load(true);
    } catch (err) {
      showToast('error', err.message);
    }
  };

  // Only words that appear in no program at all — deleting by word is safe there
  const deletableStrays = strays
    .filter((s) => s.reason === 'noword' && !s.img.readOnly)
    .map((s) => s.img);

  const handleDeleteStrays = async () => {
    const targets = deletableStrays;
    if (!targets.length) return;
    if (
      !window.confirm(
        language === 'en'
          ? `Delete ${targets.length} images whose words are in no program? Words added to students by hand will lose their image. This cannot be undone.`
          : `Удалить картинки слов, которых нет ни в одной программе (${targets.length})? Если такие слова добавлены ученикам вручную, они останутся без картинки. Это необратимо.`
      )
    )
      return;
    setBulkDeleting(true);
    let removed = 0;
    let lastError = '';
    for (const img of targets) {
      try {
        await deleteRequest(img);
        removed += 1;
      } catch (err) {
        lastError = err.message;
      }
      await sleep(150);
    }
    invalidateCache();
    setBulkDeleting(false);
    const done = `${language === 'en' ? 'Deleted' : 'Удалено'}: ${removed}`;
    showToast(
      lastError ? 'error' : 'success',
      lastError ? `${done}. ${lastError}` : done
    );
    load(true);
  };

  // ── Program view ───────────────────────────────────────────────────────────
  const q = search.trim().toLowerCase();
  const forceOpen = !!q || filter !== 'all';
  const filterCounts = { all: 0, missing: 0, done: 0 };
  const unitViews = units.map((u) => {
    const unitHit = !!q && u.labelLower.includes(q);
    const base = u.tiles.filter(
      (t) =>
        (showPhrases || !t.isPhrase) &&
        (!q || unitHit || t.w.includes(q) || t.m.includes(q))
    );
    base.forEach((t) => {
      filterCounts.all += 1;
      if (t.entry) filterCounts.done += 1;
      else filterCounts.missing += 1;
    });
    const visible =
      filter === 'all'
        ? base
        : base.filter((t) => (filter === 'done' ? !!t.entry : !t.entry));
    return { ...u, visible };
  });
  const shownUnits = unitViews.filter(
    (u) => !forceOpen || u.visible.length > 0
  );

  // Complete units start collapsed until the teacher opens them
  const isCollapsed = (u) =>
    u.key in collapsed
      ? collapsed[u.key]
      : u.total > 0 && u.withImage === u.total;
  const anyOpen = shownUnits.some((u) => !isCollapsed(u));
  const toggleUnit = (u) =>
    setCollapsed((prev) => ({ ...prev, [u.key]: !isCollapsed(u) }));
  const setAllCollapsed = (value) =>
    setCollapsed((prev) => {
      const next = { ...prev };
      units.forEach((u) => {
        next[u.key] = value;
      });
      return next;
    });

  const summary = selectedTemplate
    ? coverageById[selectedTemplate._id]
    : { total: 0, done: 0, pct: 0 };

  const renderProgramTile = (t, unitKey) => {
    const entryKey = t.entry ? imageKey(t.entry) : '';
    const target = { ...t.target, unitKey };
    const hints = [];
    if (t.entry && t.entry.readOnly)
      hints.push(
        language === 'en'
          ? 'Global image — only admin can change it'
          : 'Общая картинка — изменить может только администратор'
      );
    if (t.fallback)
      hints.push(
        language === 'en'
          ? `Showing the plain image of "${t.w}" (no meaning)`
          : `Показывается картинка слова «${t.w}» без значения`
      );
    if (t.target.meaning && !t.entry)
      hints.push(
        language === 'en'
          ? `Will be saved with meaning "${t.target.meaning}" — the word has several meanings in programs`
          : `Сохранится со значением «${t.target.meaning}» — у слова несколько значений в программах`
      );
    return (
      <WordTile
        key={t.id}
        word={t.text}
        meaning={t.meaning}
        imageUrl={t.entry ? t.entry.imageUrl : ''}
        isGlobal={!!(t.entry && t.entry.isGlobal)}
        isPhrase={t.isPhrase}
        canUpload={!t.entry || !t.entry.readOnly || t.fallback}
        canDelete={!!t.entry && !t.entry.readOnly}
        busy={!!busy[t.target.key] || (!!entryKey && !!busy[entryKey])}
        hint={hints.join('\n')}
        replaceTitle={
          t.fallback
            ? language === 'en'
              ? `Upload a separate image for "${t.meaning}"`
              : `Загрузить отдельную картинку для «${t.meaning}»`
            : ''
        }
        language={language}
        onPick={() => openPicker(target)}
        onFile={(file) => uploadForTile(target, file)}
        onDelete={() => handleDelete(t.entry)}
      />
    );
  };

  const renderUnit = (u) => {
    const open = forceOpen || !isCollapsed(u);
    const complete = u.total > 0 && u.withImage === u.total;
    const pct = u.total ? Math.round((u.withImage / u.total) * 100) : 0;
    const hasPhrases = u.tiles.some((t) => t.isPhrase);
    return (
      <Paper
        key={u.key}
        variant="outlined"
        sx={{
          mb: 1,
          borderRadius: '14px',
          overflow: 'hidden',
          boxShadow: 'none',
        }}
      >
        <Box
          role={forceOpen ? undefined : 'button'}
          tabIndex={forceOpen ? undefined : 0}
          aria-expanded={forceOpen ? undefined : open}
          onClick={forceOpen ? undefined : () => toggleUnit(u)}
          onKeyDown={
            forceOpen
              ? undefined
              : (e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    toggleUnit(u);
                  }
                }
          }
          sx={{
            px: 1.5,
            py: 1,
            cursor: forceOpen ? 'default' : 'pointer',
            userSelect: 'none',
            outline: 'none',
            '&:hover, &:focus-visible': forceOpen
              ? {}
              : { bgcolor: 'action.hover' },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {!forceOpen && (
              <ExpandMoreIcon
                fontSize="small"
                sx={{
                  color: 'text.secondary',
                  transition: 'transform 0.2s ease',
                  transform: open ? 'none' : 'rotate(-90deg)',
                }}
              />
            )}
            <Typography
              noWrap
              title={u.label}
              sx={{
                fontWeight: 700,
                fontSize: '0.92rem',
                flex: 1,
                minWidth: 0,
              }}
            >
              {u.label}
            </Typography>
            <Typography
              component="span"
              sx={{
                fontSize: '0.75rem',
                fontWeight: 600,
                whiteSpace: 'nowrap',
                color: complete ? 'success.main' : 'text.secondary',
              }}
            >
              {u.total > 0
                ? `${u.withImage}/${u.total} ${language === 'en' ? 'with image' : 'с картинкой'}`
                : language === 'en'
                  ? 'phrases only'
                  : 'только фразы'}
            </Typography>
          </Box>
          {u.total > 0 && (
            <LinearProgress
              variant="determinate"
              value={pct}
              color={complete ? 'success' : 'primary'}
              sx={{ mt: 0.75, height: 3 }}
            />
          )}
        </Box>
        <Collapse in={open} timeout={180} unmountOnExit>
          <Box sx={{ px: 1.5, pt: 0.5, pb: 1.5 }}>
            {u.visible.length > 0 ? (
              <Box sx={TILE_GRID_SX}>
                {u.visible.map((t) => renderProgramTile(t, u.key))}
              </Box>
            ) : (
              <Typography variant="caption" color="text.secondary">
                {hasPhrases && !showPhrases
                  ? language === 'en'
                    ? 'Only phrases here — turn on “Show phrases”'
                    : 'Здесь только фразы — включите «Показывать фразы»'
                  : language === 'en'
                    ? 'No words'
                    : 'Нет слов'}
              </Typography>
            )}
          </Box>
        </Collapse>
      </Paper>
    );
  };

  // ── All-words view ─────────────────────────────────────────────────────────
  const matchesImage = (img) =>
    !q || norm(img.word).includes(q) || norm(img.meaning).includes(q);
  const allFiltered = sortedImages.filter(matchesImage);
  const straysFiltered = strays.filter((s) => matchesImage(s.img));

  const strayNote = (reason) => {
    if (reason === 'noword')
      return language === 'en' ? 'not in programs' : 'нет в программах';
    if (reason === 'meaning')
      return language === 'en'
        ? 'meaning does not match'
        : 'значение не совпадает';
    return language === 'en'
      ? 'overridden by meanings'
      : 'перекрыта значениями';
  };

  const renderImageTile = (img, note) => {
    const key = imageKey(img);
    const target = { key, word: norm(img.word), meaning: norm(img.meaning) };
    const where = usage.used.get(key);
    const hints = [];
    if (img.readOnly)
      hints.push(
        language === 'en'
          ? 'Global image — only admin can change it'
          : 'Общая картинка — изменить может только администратор'
      );
    if (where) {
      hints.push(...where.slice(0, 6));
      if (where.length > 6) hints.push(`+${where.length - 6}`);
    } else if (templatesReady) {
      hints.push(
        language === 'en'
          ? 'Not used in any program'
          : 'Не используется в программах'
      );
    }
    return (
      <WordTile
        key={key}
        word={img.word}
        meaning={img.meaning}
        imageUrl={img.imageUrl}
        isGlobal={!!img.isGlobal}
        isPhrase={false}
        canUpload={!img.readOnly}
        canDelete={!img.readOnly}
        busy={!!busy[key]}
        hint={hints.join('\n')}
        note={note}
        replaceTitle=""
        language={language}
        onPick={() => openPicker(target)}
        onFile={(file) => uploadForTile(target, file)}
        onDelete={() => handleDelete(img)}
      />
    );
  };

  const spinner = (
    <Box sx={{ textAlign: 'center', py: 8 }}>
      <CircularProgress />
    </Box>
  );

  const searchField = (
    <TextField
      size="small"
      placeholder={
        view === 'programs'
          ? language === 'en'
            ? 'Search this program…'
            : 'Поиск по программе…'
          : language === 'en'
            ? 'Search words...'
            : 'Поиск...'
      }
      value={search}
      onChange={(e) => setSearch(e.target.value)}
      slotProps={{
        input: {
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
        },
      }}
      sx={{ flex: '1 1 200px', maxWidth: { sm: 280 } }}
    />
  );

  let programView;
  if (templatesLoading || loading) {
    programView = spinner;
  } else if (templatesError) {
    programView = (
      <Alert severity="warning">
        {language === 'en'
          ? 'Could not load programs. The full list is still available under “All words”.'
          : 'Не удалось загрузить программы. Полный список доступен во вкладке «Все слова».'}
      </Alert>
    );
  } else if (!selectedTemplate) {
    programView = (
      <Typography color="text.secondary" sx={{ textAlign: 'center', py: 6 }}>
        {language === 'en'
          ? 'No programs with words yet.'
          : 'Пока нет программ со словами.'}
      </Typography>
    );
  } else {
    programView = (
      <>
        <Tabs
          value={selectedTemplate._id}
          onChange={(_, id) => selectTemplate(id)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            minHeight: 40,
            mb: 1.5,
            borderBottom: 1,
            borderColor: 'divider',
            '& .MuiTab-root': { minHeight: 40, py: 0.5, px: 1.5 },
          }}
        >
          {sortedTemplates.map((tpl) => {
            const cov = coverageById[tpl._id];
            return (
              <Tab
                key={tpl._id}
                value={tpl._id}
                label={
                  <Box
                    component="span"
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'baseline',
                      gap: 0.75,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {tpl.name}
                    {cov && cov.total > 0 && (
                      <Box
                        component="span"
                        sx={{
                          fontSize: '0.72rem',
                          fontWeight: 600,
                          color:
                            cov.pct === 100 ? 'success.main' : 'text.secondary',
                        }}
                      >
                        {cov.pct}%
                      </Box>
                    )}
                  </Box>
                }
              />
            );
          })}
        </Tabs>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: { xs: 1, sm: 2 },
            flexWrap: 'wrap',
            mb: 1.5,
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {language === 'en'
              ? `${summary.total} words in this program, ${summary.done} with images (${summary.pct}%)`
              : `В программе ${summary.total} ${plural(summary.total, 'слово', 'слова', 'слов')}, с картинкой ${summary.done} (${summary.pct}%)`}
          </Typography>
          <LinearProgress
            variant="determinate"
            value={summary.pct}
            color={summary.pct === 100 ? 'success' : 'primary'}
            sx={{ flex: '1 1 140px', maxWidth: 320, height: 6 }}
          />
        </Box>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            flexWrap: 'wrap',
            mb: 2,
          }}
        >
          {searchField}
          {[
            ['all', language === 'en' ? 'All' : 'Все'],
            ['missing', language === 'en' ? 'No image' : 'Без картинки'],
            ['done', language === 'en' ? 'With image' : 'С картинкой'],
          ].map(([value, label]) => (
            <Chip
              key={value}
              size="small"
              clickable
              label={`${label} · ${filterCounts[value]}`}
              color={filter === value ? 'primary' : 'default'}
              variant={filter === value ? 'filled' : 'outlined'}
              onClick={() => setFilter(value)}
            />
          ))}
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={showPhrases}
                onChange={(e) => setShowPhrases(e.target.checked)}
              />
            }
            label={
              <Typography variant="body2">
                {language === 'en' ? 'Show phrases' : 'Показывать фразы'}
              </Typography>
            }
            sx={{ mx: 0 }}
          />
          <Box sx={{ flexGrow: 1 }} />
          {!forceOpen && shownUnits.length > 1 && (
            <Button
              size="small"
              color="inherit"
              onClick={() => setAllCollapsed(anyOpen)}
              startIcon={anyOpen ? <UnfoldLessIcon /> : <UnfoldMoreIcon />}
              sx={{ py: 0.5, px: 1.5 }}
            >
              {anyOpen
                ? language === 'en'
                  ? 'Collapse all'
                  : 'Свернуть все'
                : language === 'en'
                  ? 'Expand all'
                  : 'Развернуть все'}
            </Button>
          )}
        </Box>

        {shownUnits.length === 0 ? (
          <Typography
            color="text.secondary"
            sx={{ textAlign: 'center', py: 6 }}
          >
            {language === 'en' ? 'Nothing found' : 'Ничего не найдено'}
          </Typography>
        ) : (
          shownUnits.map(renderUnit)
        )}
      </>
    );
  }

  const allView = loading ? (
    spinner
  ) : (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          mb: 2,
          flexWrap: 'wrap',
        }}
      >
        {searchField}
        <Chip
          label={`${allFiltered.length} ${language === 'en' ? 'words' : plural(allFiltered.length, 'слово', 'слова', 'слов')}`}
          variant="outlined"
          size="small"
        />
        {templatesReady && images.length > 0 && (
          <Typography variant="caption" color="text.secondary">
            {language === 'en'
              ? `in programs: ${images.length - strays.length} · unused: ${strays.length}`
              : `в программах: ${images.length - strays.length} · не используются: ${strays.length}`}
          </Typography>
        )}
        <Box sx={{ flexGrow: 1 }} />
        {images.some((i) => !i.readOnly) && (
          <Button
            size="small"
            color="error"
            variant="outlined"
            startIcon={<DeleteIcon />}
            onClick={handleDeleteAll}
            disabled={uploading || bulkDeleting}
            sx={{ py: 0.5, px: 1.5 }}
          >
            {language === 'en' ? 'Delete all' : 'Удалить все'}
          </Button>
        )}
      </Box>

      {strays.length > 0 && (
        <Paper
          variant="outlined"
          sx={(theme) => ({
            mb: 2,
            borderRadius: '14px',
            boxShadow: 'none',
            overflow: 'hidden',
            borderColor: alpha(theme.palette.warning.main, 0.5),
            bgcolor: alpha(theme.palette.warning.main, 0.05),
          })}
        >
          <Box
            role="button"
            tabIndex={0}
            aria-expanded={strayOpen}
            onClick={() => setStrayOpen((v) => !v)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setStrayOpen((v) => !v);
              }
            }}
            sx={{
              px: 1.5,
              py: 1,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              flexWrap: 'wrap',
              cursor: 'pointer',
              userSelect: 'none',
              outline: 'none',
            }}
          >
            <ExpandMoreIcon
              fontSize="small"
              sx={{
                color: 'text.secondary',
                transition: 'transform 0.2s ease',
                transform: strayOpen ? 'none' : 'rotate(-90deg)',
              }}
            />
            <Typography sx={{ fontWeight: 700, fontSize: '0.92rem' }}>
              {language === 'en'
                ? 'Not used in any program'
                : 'Не используются в программах'}
            </Typography>
            <Chip size="small" label={strays.length} sx={{ height: 20 }} />
            <Box sx={{ flexGrow: 1 }} />
            {deletableStrays.length > 0 && (
              <Button
                size="small"
                color="error"
                variant="outlined"
                disabled={bulkDeleting || uploading}
                startIcon={
                  bulkDeleting ? (
                    <CircularProgress size={14} color="inherit" />
                  ) : (
                    <DeleteIcon />
                  )
                }
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteStrays();
                }}
                sx={{ py: 0.25, px: 1.5 }}
              >
                {language === 'en'
                  ? `Delete words not in programs (${deletableStrays.length})`
                  : `Удалить слова вне программ (${deletableStrays.length})`}
              </Button>
            )}
          </Box>
          <Collapse in={strayOpen} timeout={180} unmountOnExit>
            <Box sx={{ px: 1.5, pb: 1.5 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mb: 1.25 }}
              >
                {language === 'en'
                  ? 'Speed mode never shows these for program words: the word is in no unit, or the saved meaning differs from the program.'
                  : 'Быстрая оценка не покажет их для слов программ: слова нет ни в одном юните или сохранённое значение не совпадает с программой.'}
              </Typography>
              {straysFiltered.length > 0 ? (
                <Box sx={TILE_GRID_SX}>
                  {straysFiltered.map((s) =>
                    renderImageTile(s.img, strayNote(s.reason))
                  )}
                </Box>
              ) : (
                <Typography variant="caption" color="text.secondary">
                  {language === 'en' ? 'Nothing found' : 'Ничего не найдено'}
                </Typography>
              )}
            </Box>
          </Collapse>
        </Paper>
      )}

      {allFiltered.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography color="text.secondary">
            {search
              ? language === 'en'
                ? 'No words match your search'
                : 'Ничего не найдено'
              : language === 'en'
                ? 'No images yet. Upload some above!'
                : 'Нет изображений. Загрузите выше!'}
          </Typography>
        </Box>
      ) : (
        <Box sx={TILE_GRID_SX}>
          {allFiltered.map((img) => renderImageTile(img, ''))}
        </Box>
      )}
    </>
  );

  return (
    <Layout
      title={language === 'en' ? 'Word Dictionary' : 'Словарь изображений'}
    >
      <Container maxWidth="lg" sx={{ py: { xs: 2, sm: 3 } }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: { xs: 'flex-start', sm: 'flex-end' },
            justifyContent: 'space-between',
            gap: 1.5,
            flexWrap: 'wrap',
            mb: 2,
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h5" component="h1" sx={{ mb: 0.25 }}>
              {language === 'en'
                ? 'Word Image Dictionary'
                : 'Словарь изображений'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {language === 'en'
                ? 'Upload images for words. They appear automatically during speed assessments.'
                : 'Загрузите изображения для слов. Они автоматически показываются во время быстрой оценки.'}
            </Typography>
          </Box>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={view}
            onChange={(_, v) => {
              if (v) setView(v);
            }}
            sx={{ '& .MuiToggleButton-root': { px: 1.5, py: 0.5 } }}
          >
            <ToggleButton value="programs">
              {language === 'en' ? 'By program' : 'По программам'}
            </ToggleButton>
            <ToggleButton value="all">
              {language === 'en' ? 'All words' : 'Все слова'}
              {!loading && ` · ${images.length}`}
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {/* Bulk upload */}
        <Paper
          variant="outlined"
          sx={{
            p: { xs: 1.25, sm: 1.5 },
            mb: 2.5,
            borderRadius: '16px',
            boxShadow: 'none',
          }}
        >
          <Box
            onDrop={handleDrop}
            onDragOver={(e) => {
              e.preventDefault();
              setDragOver(true);
            }}
            onDragLeave={() => setDragOver(false)}
            onClick={() => fileInputRef.current?.click()}
            sx={(theme) => ({
              border: '1.5px dashed',
              borderColor: dragOver ? 'primary.main' : 'divider',
              borderRadius: '12px',
              px: { xs: 1.5, sm: 2 },
              py: 1.25,
              cursor: 'pointer',
              bgcolor: dragOver
                ? alpha(theme.palette.primary.main, 0.06)
                : 'transparent',
              transition: 'all 0.2s',
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              '&:hover': { borderColor: 'primary.main' },
            })}
          >
            <AddPhotoAlternateIcon color="action" />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                {language === 'en' ? 'Bulk Upload' : 'Массовая загрузка'}
                <Box
                  component="span"
                  sx={{ fontWeight: 400, color: 'text.secondary' }}
                >
                  {' — '}
                  {language === 'en'
                    ? 'click or drag images here (multiple allowed)'
                    : 'кликните или перетащите изображения (можно несколько)'}
                </Box>
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: { xs: 'none', sm: 'block' } }}
              >
                {language === 'en'
                  ? 'Filename becomes the word (underscore → space), e.g. red_apple.jpg → "red apple". Review before saving. Or drop an image straight onto a word below.'
                  : 'Имя файла становится словом (подчёркивание → пробел), напр. red_apple.jpg → "red apple". Проверьте перед сохранением. Или перетащите картинку прямо на слово ниже.'}
              </Typography>
            </Box>
            {queue.length > 0 && (
              <Chip
                size="small"
                color="primary"
                label={`${queue.length} ${language === 'en' ? 'queued' : 'в очереди'}`}
              />
            )}
          </Box>
          <input
            ref={fileInputRef}
            type="file"
            accept={ALLOWED_TYPES.join(',')}
            multiple
            style={{ display: 'none' }}
            onChange={(e) => {
              addFiles(e.target.files);
              e.target.value = '';
            }}
          />

          {queue.length > 0 && (
            <Box sx={{ mt: 1.5 }}>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    sm: 'repeat(2, minmax(0, 1fr))',
                    md: 'repeat(3, minmax(0, 1fr))',
                  },
                  gap: 1,
                }}
              >
                {queue.map((item) => {
                  const hint = queueHint(item);
                  return (
                    <Paper
                      key={item.id}
                      variant="outlined"
                      sx={{
                        p: 1,
                        display: 'flex',
                        gap: 1,
                        alignItems: 'center',
                        borderRadius: '12px',
                        boxShadow: 'none',
                        borderColor:
                          item.status === 'error'
                            ? 'error.main'
                            : item.status === 'done'
                              ? 'success.main'
                              : 'divider',
                      }}
                    >
                      <Box
                        component="img"
                        src={item.preview}
                        alt={item.word}
                        sx={{
                          width: 48,
                          height: 48,
                          objectFit: 'contain',
                          borderRadius: '8px',
                          flexShrink: 0,
                          bgcolor: '#fff',
                        }}
                      />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <TextField
                          value={item.word}
                          onChange={(e) =>
                            updateQueueItem(item.id, { word: e.target.value })
                          }
                          size="small"
                          fullWidth
                          variant="standard"
                          disabled={item.status === 'uploading'}
                        />
                        <TextField
                          value={item.meaning}
                          onChange={(e) =>
                            updateQueueItem(item.id, {
                              meaning: e.target.value,
                            })
                          }
                          size="small"
                          fullWidth
                          variant="standard"
                          placeholder={
                            language === 'en'
                              ? 'meaning (optional)'
                              : 'значение (необязательно)'
                          }
                          disabled={item.status === 'uploading'}
                        />
                        {item.status === 'uploading' && (
                          <Typography variant="caption" color="primary">
                            {item.error ||
                              (language === 'en' ? 'Uploading…' : 'Загрузка…')}
                          </Typography>
                        )}
                        {item.status === 'error' && (
                          <Typography
                            variant="caption"
                            color="error"
                            noWrap
                            title={item.error}
                            sx={{ display: 'block' }}
                          >
                            {item.error}
                          </Typography>
                        )}
                        {item.status === 'done' && (
                          <Typography variant="caption" color="success.main">
                            {language === 'en' ? 'Saved' : 'Сохранено'}
                          </Typography>
                        )}
                        {hint && (
                          <Typography
                            variant="caption"
                            noWrap
                            title={hint.text}
                            sx={{ display: 'block', color: hint.color }}
                          >
                            {hint.text}
                          </Typography>
                        )}
                      </Box>
                      <IconButton
                        size="small"
                        onClick={() => removeQueueItem(item.id)}
                        disabled={item.status === 'uploading'}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Paper>
                  );
                })}
              </Box>
              <Box
                sx={{
                  mt: 1.5,
                  display: 'flex',
                  gap: 1,
                  justifyContent: 'flex-end',
                }}
              >
                <Button
                  onClick={() => setQueue([])}
                  disabled={uploading}
                  color="inherit"
                  size="small"
                >
                  {language === 'en' ? 'Clear' : 'Очистить'}
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={
                    uploading ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <CloudUploadIcon />
                    )
                  }
                  onClick={handleUpload}
                  disabled={
                    uploading || queue.every((item) => item.status === 'done')
                  }
                >
                  {uploading
                    ? language === 'en'
                      ? 'Saving…'
                      : 'Сохраняем…'
                    : language === 'en'
                      ? `Save all (${queue.filter((item) => item.status !== 'done').length})`
                      : `Сохранить все (${queue.filter((item) => item.status !== 'done').length})`}
                </Button>
              </Box>
            </Box>
          )}

          {error && (
            <Alert
              severity="error"
              sx={{ mt: 1.5 }}
              onClose={() => setError('')}
            >
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" sx={{ mt: 1.5 }}>
              {success}
            </Alert>
          )}
        </Paper>

        {view === 'programs' ? programView : allView}

        <input
          ref={tileInputRef}
          type="file"
          accept={ALLOWED_TYPES.join(',')}
          style={{ display: 'none' }}
          onChange={(e) => {
            const file = e.target.files && e.target.files[0];
            e.target.value = '';
            const target = tileTargetRef.current;
            tileTargetRef.current = null;
            if (file && target) uploadForTile(target, file);
          }}
        />
      </Container>

      <Snackbar
        open={toast.open}
        autoHideDuration={toast.severity === 'error' ? 6000 : 2500}
        onClose={(_, reason) => {
          if (reason !== 'clickaway')
            setToast((prev) => ({ ...prev, open: false }));
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={toast.severity}
          variant="filled"
          onClose={() => setToast((prev) => ({ ...prev, open: false }))}
          sx={{ width: '100%' }}
        >
          {toast.text}
        </Alert>
      </Snackbar>
    </Layout>
  );
}
